import sys from '@truckdown/jsclient';
import * as icons from '@truckdown/jsclient/icons';
import * as apis from '@truckdown/jsclient/apis';
import * as locations from '@truckdown/locations';
import { defineComponent, ref, computed, reactive } from 'vue';
import { email, required, numeric, minValue, maxValue, helpers } from '@vuelidate/validators';
import { useVuelidate } from '@vuelidate/core';
import { ILegacyListingService } from '../Services/ILegacyListingService';
import { AddRatingModel } from '../Services/AddRatingModel';

sys.fontLibrary.add(icons.solid.faBullhorn);
sys.fontLibrary.add(icons.solid.faUserLock);
sys.fontLibrary.add(icons.solid.faXmark);

var setupUserData = function () {
    var ctx = sys.framework.context;

    const isAuth = computed(() => {
        return ctx.isAuthenticated();
    });
    const isAce = computed(() => {
        return ctx.isInRole(apis.framework.SecurityConstants.Roles.FleetUserRole);
    });

    return {
        isAuth,
        isAce
    };
}

export default defineComponent({
    props: {
        listing: {
            type: Object as () => locations.Listing,
            required: true
        },
        initialValue: Number
    },
    setup: function (props, ctx) {
        let listing = props.listing as locations.Listing;

        const type = ref<'internal' | 'public'>('internal');
        const message = ref<string>('');

        const selectInternal = function () {
            type.value = 'internal';
        }

        const selectPublic = function () {
            type.value = 'public';
        }

        const noteLabel = computed(() => {
            if (type.value == 'internal') {
                return 'Note: ';
            }
            return 'Comment to send to vendor: ';
        });

        const saveLabel = computed(() => {
            if (type.value == 'internal') {
                return 'Save Rating';
            }
            return 'Send Rating';
        });

        const state = reactive({
            note: '',
            value: props.initialValue ?? 0
        });

        const displayValue = ref<number>(state.value);

        const validationRules = {
            note: {
                required: helpers.withMessage('Please provide a note/comment', required)
            },
            value: {
                required: helpers.withMessage('Please set a rating value', required),
                numeric,
                minValue: helpers.withMessage('Please set a rating value', minValue(0.5)),
                maxValue: helpers.withMessage('Please set a rating value', maxValue(5))
            }
        };

        const v$ = useVuelidate(validationRules, state)

        const control_width = 50.75;

        const isHalf = function (x: number) {
            return x < (control_width / 2);
        }

        const setValue = function (val: number, ev: MouseEvent) {
            if (isHalf(ev.offsetX)) {
                state.value = val - 0.5;
            } else {
                state.value = val;
            }

            displayValue.value = state.value;
        }

        const setDisplayValue = function (val: number, ev: MouseEvent) {
            if (isHalf(ev.offsetX)) {
                displayValue.value = val - 0.5;
            } else {
                displayValue.value = val;
            }
        }

        const resetDisplayValue = function () {
            displayValue.value = state.value;
        }

        const ratingTitle = computed(() => {
            return 'Set rating to ' + displayValue.value;
        });

        const submit = async function (ev: Event) {
            const isFormCorrect = await v$.value.$validate();

            if (isFormCorrect) {
                var isAce = sys.framework.context.isInRole(apis.framework.SecurityConstants.Roles.FleetUserRole)

                var service = sys.framework.getRequiredService<ILegacyListingService>('ILegacyListingService');
                var model = new AddRatingModel(
                    listing.id,
                    state.value,
                    type.value == 'public',
                    state.note
                );
                var res = await service.addRating(model);
                if (res != null) {
                    if (res.note != null) {
                        let note: locations.ListingNote = {
                            id: res.note._id,
                            comment: res.note.c,
                            date: res.note.d,
                            user: res.note.n,
                            userId: res.note.uid
                        }
                        let notes = listing.notes;
                        notes.splice(0, 0, note);
                        sys.events.emit('update-notes', listing.id, notes);
                    }

                    if (setPreferred.value) {
                        if (state.value < 3) {
                            let pref = await service.addPreferred(listing.id, true);
                            if (pref != null && isAce) {
                                let preference: locations.PreferenceModel = {
                                    type: pref.t,
                                    priority: pref.p,
                                    radius: pref.r,
                                    services: pref.scvs
                                };
                                sys.events.emit('update-preference', listing.id, preference);
                            }
                        } else if (state.value > 3.5) {
                            let pref = await service.addPreferred(listing.id, false);
                            if (pref != null && isAce) {
                                let preference: locations.PreferenceModel = {
                                    type: pref.t,
                                    priority: pref.p,
                                    radius: pref.r,
                                    services: pref.scvs
                                };
                                sys.events.emit('update-preference', listing.id, preference);
                            }
                        } else {
                            let pref = await service.deletePreferred(listing.id);
                            if (pref.ok && isAce) {
                                let preference: locations.PreferenceModel = {
                                    type: locations.PreferenceType.None,
                                    services: []
                                };
                                sys.events.emit('update-preference', listing.id, preference);
                            }
                        }
                    }
                    message.value = 'Your rating has been submitted.';
                } else {
                    message.value = 'There was an error submitting your rating.';
                }
            }
        }

        const closeForm = function (ev: Event) {
            sys.events.emit('close-listing-form', props.listing.id);
        }

        const setPreferred = ref<boolean>(true);

        const showAddAvoid = computed(() => {
            if (listing.properties.preference?.type != locations.PreferenceType.Avoid) {
                return state.value < 3;
            }
            return false;
        });

        const showAddFavorite = computed(() => {
            if (listing.properties.preference?.type != locations.PreferenceType.Preferred) {
                return state.value > 3.5;
            }
            return false;
        });

        const showClearPreferred = computed(() => {
            let type = listing.properties.preference?.type ?? locations.PreferenceType.None;
            if (type != locations.PreferenceType.None) {
                return state.value < 4 && state.value > 2.5;
            }
            return false;
        });

        const labelClearPreferred = computed(() => {
            if (listing.properties.preference?.type == locations.PreferenceType.Avoid) {
                return 'Remove this vendor from your avoid list.';
            }
            if (listing.properties.preference?.type == locations.PreferenceType.Preferred) {
                return 'Remove this vendor from your favorite list.';
            }
            return '';
        });

        const moreInfoUrl = computed(() => {
            if (sys.framework.context.sys == 'planrv') {
                return '/shop/features';
            }
            return '/shop/fleets';
        });

        return {
            ...setupUserData(),
            type,
            message,
            selectInternal,
            selectPublic,
            noteLabel,
            saveLabel,
            ratingTitle,
            state,
            displayValue,
            v$,
            setValue,
            setDisplayValue,
            resetDisplayValue,
            submit,
            closeForm,
            setPreferred,
            showClearPreferred,
            showAddAvoid,
            showAddFavorite,
            labelClearPreferred,
            moreInfoUrl
        }
    }
});
